<template>
  <teleport to="head">
    <MetaTags :meta-data-fn="metaDataFn" />
  </teleport>
  <div class="max-w-full min-h-[100vh] flex flex-col h-screen overflow-hidden">
    <Transition
      enter-active-class="transition-opacity duration-250 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-250 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <Sidebar v-if="mounted && userStore.isSidebarVisible" />
    </Transition>
    <div v-if="!disableSSR" class="scroll-container w-full overflow-y-auto">
      <div class="min-h-[100vh] flex flex-col">
        <Header />
        <main class="flex-grow flex flex-col">
          <RouterView />
        </main>
      </div>
      <div>
        <Footer />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MetaTags } from '@/components/MetaTags'
import { useMeta } from '@/composables/Meta'
import { computed, getCurrentInstance, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import Config from '@/utils/config'
import { absoluteUrl } from '@/utils/canonical'
import Footer from '@/components/Footer.vue'
import Header from '@/components/Header.vue'
import Sidebar from '@/components/Sidebar.vue'
import { usePublisherStore } from '@/stores/PublisherStore'

const { metaDataFn, updateMetaData } = useMeta()
const { t } = useI18n()
const app = getCurrentInstance()
const fullUrl = app?.appContext.config.globalProperties.fullUrl
const userStore = useUserStore()
const route = useRoute()

// Gates the logged-in chrome. false during SSR and the client's first
// (hydration) render; the token comes from a persisted store the server cannot
// see, so branching on it during hydration mismatches.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})
usePublisherStore()

onServerPrefetch(() => {
  if (!language.value) return
  userStore.setLanguage(language.value)
})

const disableSSR = computed(() => {
  if (!Config.ssr) return false
  return route.matched.some((record) => {
    return record.meta.ssr != undefined && record.meta.ssr === false
  })
})

const language = computed((): string => {
  return (route.params.locale as string) ?? userStore.language ?? Config.languages?.[0]
})

// Per-route SEO lives here rather than in the views: this resets on every
// route change, so a view setting its own meta would race the reset and
// mismatch during hydration.
const seo = computed(() => {
  switch (route.name) {
    case 'home':
      return { title: t('landing.seo.title'), description: t('landing.seo.description') }
    case 'imprint':
      return { title: t('legal.imprint.seoTitle'), description: t('legal.imprint.seoDescription') }
    case 'privacy':
      return { title: t('legal.privacy.seoTitle'), description: t('legal.privacy.seoDescription') }
    case 'contact':
      return { title: t('contact.seoTitle'), description: t('contact.seoDescription') }
    default:
      return { title: t('common.seo.title'), description: t('common.seo.description') }
  }
})

const resetMetaData = () => {
  return updateMetaData({
    language: computed(() => userStore.language),
    title: computed(() => seo.value.title),
    description: computed(() => seo.value.description),
    image: computed(() => absoluteUrl(t('common.seo.image'))),
    // Nothing behind a login belongs in an index, and an ssr:false route is
    // exactly that here: it server-renders an empty body, which is the worst
    // thing a search result can point at.
    noIndex: computed(() => route.matched.some(
      (record) => record.meta.noIndex === true || record.meta.ssr === false
    )),
    url: computed(() => (Config.ssr ? fullUrl : window.location.href)),
    next: computed(() => undefined),
    prev: computed(() => undefined),
  })
}

resetMetaData()

watch(
  () => route.path,
  () => {
    resetMetaData()
  }
)

watch(
  () => language.value,
  (newLanguage) => {
    if (!newLanguage) return
    userStore.setLanguage(newLanguage)
  },
  { immediate: true }
)
</script>
